import { useEffect, useState } from "react";

import { RingProgress } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { Text } from "components";
import { DashboardService } from "services";
import { DashboardAgentKpiMeterResponse } from "types";

import classes from "./KpiSection.module.css";

interface KpiMetricDefinition {
  labelKey: string;
  metric: string;
}

interface KpiMetricItem {
  labelKey: string;
  value: number;
  target: number | null;
}

interface KpiGroupDefinition {
  titleKey: string;
  responseKey: "daily" | "monthly";
  metrics: readonly KpiMetricDefinition[];
}

interface KpiGroupItem {
  titleKey: string;
  metrics: KpiMetricItem[];
}

const DAILY_KPI_METRICS: readonly KpiMetricDefinition[] = [
  {
    labelKey: "dashboard.kpis.metrics.qualifications",
    metric: "qualifications",
  },
  {
    labelKey: "dashboard.kpis.metrics.leads",
    metric: "leadsInBank",
  },
];

const MONTHLY_KPI_METRICS: readonly KpiMetricDefinition[] = [
  {
    labelKey: "dashboard.kpis.metrics.approved",
    metric: "approved",
  },
  {
    labelKey: "dashboard.kpis.metrics.disbursed",
    metric: "disbursedCount",
  },
] as const;

const KPI_GROUPS: readonly KpiGroupDefinition[] = [
  {
    titleKey: "dashboard.kpis.daily",
    responseKey: "daily",
    metrics: DAILY_KPI_METRICS,
  },
  {
    titleKey: "dashboard.kpis.monthly",
    responseKey: "monthly",
    metrics: MONTHLY_KPI_METRICS,
  },
] as const;

const createEmptyMetrics = (metrics: readonly KpiMetricDefinition[]): KpiMetricItem[] =>
  metrics.map((metric) => ({
    labelKey: metric.labelKey,
    value: 0,
    target: null,
  }));

const createEmptyGroups = (): KpiGroupItem[] =>
  KPI_GROUPS.map((group) => ({
    titleKey: group.titleKey,
    metrics: createEmptyMetrics(group.metrics),
  }));

const mapKpiMetersToMetrics = (
  metrics: readonly KpiMetricDefinition[],
  meters: DashboardAgentKpiMeterResponse[],
): KpiMetricItem[] => {
  const metersByMetric = meters.reduce<Record<string, DashboardAgentKpiMeterResponse>>((acc, item) => {
    acc[item.metric] = item;
    return acc;
  }, {});

  return metrics.map((metric) => {
    const data = metersByMetric[metric.metric];

    return {
      labelKey: metric.labelKey,
      value: typeof data?.value === "number" ? data.value : 0,
      target: typeof data?.target === "number" ? data.target : null,
    };
  });
};

const getProgressValue = (value: number, target: number | null) => {
  if (target === null || target <= 0) {
    return 0;
  }

  return Math.min((value / target) * 100, 100);
};

const getKpiColor = (value: number, target: number | null) => {
  if (target === null || target <= 0) {
    return "#d9dee4";
  }

  if (value > target) {
    return "#71bf44";
  }

  if (value === target) {
    return "#d4c84a";
  }

  return "#b24545";
};

const KpiMetric = ({ labelKey, value, target }: KpiMetricItem) => {
  const { t } = useTranslation();
  const color = getKpiColor(value, target);

  return (
    <div className={classes.metric}>
      <div className={classes.ring}>
        <RingProgress
          size={82}
          thickness={6}
          roundCaps
          sections={[{ value: getProgressValue(value, target), color }]}
          rootColor="#d9dee4"
        />
        <div className={classes.ringValue}>{`${value}/${target ?? "–"}`}</div>
      </div>
      <Text className={classes.metricLabel}>{t(labelKey)}</Text>
    </div>
  );
};

const KpiSection = () => {
  const { t } = useTranslation();
  const [groups, setGroups] = useState<KpiGroupItem[]>(createEmptyGroups);

  useEffect(() => {
    let mounted = true;

    const loadKpis = async () => {
      try {
        const data = await DashboardService.getAgentKpis();

        if (!mounted) {
          return;
        }

        setGroups(
          KPI_GROUPS.map((group) => ({
            titleKey: group.titleKey,
            metrics: mapKpiMetersToMetrics(group.metrics, data[group.responseKey] ?? []),
          })),
        );
      } catch {
        if (mounted) {
          setGroups(createEmptyGroups());
        }
      }
    };

    loadKpis();

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div className={classes.root}>
      {groups.map((group) => (
        <section key={group.titleKey} className={classes.group}>
          <Text className={classes.headerLabel}>{t(group.titleKey)}</Text>

          <div className={classes.metrics}>
            {group.metrics.map((item) => (
              <KpiMetric key={item.labelKey} {...item} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};

export default KpiSection;
